import { DRAWING_LIMITS, type BrushKind, type DrawingDTO, type StrokeDTO } from '@shared/protocol/messages';
import { Emitter } from '../game/core/Emitter';
import { GRID, drawStroke, emptyDrawing, pointCount, renderDrawing, simplify, validateDrawing } from './strokes';
import { stampStrokes, type StampId } from './stamps';
import { fillAt, mirrorStroke, pixelFillAt } from './fill';
import { arrangeOrder, boundsOf, clampMove, hitStroke, moved, pointBounds, strokesInBox, withGroups, type Arrange, type Box } from './select';
import { t } from '../i18n';

export const PALETTE = ['#3b3b3b', '#e4572e', '#f3a712', '#f6d55c', '#6ab04c', '#3caea3', '#2e86de', '#8e5bd6', '#ff8fab', '#9c6644', '#222222', '#ffffff'] as const;
export const BRUSHES = [3, 6, 12, 20] as const;
/** Easy mode: only big, forgiving brushes. Pro mode: fine to thick. */
export const EASY_BRUSHES = [6, 12, 20] as const;
export const PRO_BRUSHES = [1, 3, 6, 12, 20, 32] as const;
/** Pixel art square sizes in grid units: a 64×64, 32×32 or 16×16 picture. */
export const PIXEL_SIZES = [4, 8, 16] as const;
/** Every brush look, in the order the creator shows them. */
export const BRUSH_LOOKS: readonly BrushKind[] = ['pencil', 'marker', 'crayon', 'spray', 'watercolor', 'calligraphy', 'pixel'];

export type DrawTool = 'pencil' | 'eraser' | 'stamp' | 'fill' | 'move';

/**
 * Ink stops this far below the drawing limits; the rest is kept for the
 * eraser, so a full page can still be fixed instead of drawn again.
 */
export const ERASER_RESERVE = { strokes: 100, points: 2000 } as const;

/** Would these strokes still fit? Ink must leave the eraser's reserve free. */
export function fitsLimits(strokes: readonly StrokeDTO[], eraser: boolean): boolean {
  const spare = eraser ? { strokes: 0, points: 0 } : ERASER_RESERVE;
  if (strokes.length > DRAWING_LIMITS.maxStrokes - spare.strokes) return false;
  return pointCount({ w: GRID, h: GRID, strokes: strokes as StrokeDTO[] }) <= DRAWING_LIMITS.maxPoints - spare.points;
}

type HistoryEntry =
  | { kind: 'add'; strokes: StrokeDTO[]; group: number }
  | { kind: 'clear'; prev: StrokeDTO[]; prevGroups: number[] }
  | { kind: 'move'; idx: number[]; dx: number; dy: number }
  | { kind: 'remove'; items: { at: number; stroke: StrokeDTO; group: number }[] }
  | { kind: 'order'; order: number[] };

/**
 * Pencil/eraser drawing surface. Strokes are recorded as vectors on a
 * 256×256 grid, so drawings stay small, can be undone/redone, re-edited,
 * validated by the server and re-traced for line-boil animation.
 *
 * Strokes added together (a stamp, a fill, a mirrored line) share a group id
 * (kept only while editing), so the move tool picks them up as one piece.
 */
export class DrawingCanvas extends Emitter<{ change: DrawingDTO; limit: string; selection: number }> {
  readonly el: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private strokes: StrokeDTO[] = [];
  private groups: number[] = [];
  private nextGroup = 1;
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private current: StrokeDTO | null = null;
  private pointerId: number | null = null;
  private readonly res: number;
  /** Move tool: selected stroke indices, a drag in progress, or a selection box being drawn. */
  private selection: number[] = [];
  private drag: { sx: number; sy: number; dx: number; dy: number } | null = null;
  private box: Box | null = null;
  private toolValue: DrawTool = 'pencil';
  color: string = PALETTE[0];
  size: number = BRUSHES[1];
  /** RGB pens: each new stroke, stamp or fill takes the next of these colours (null = plain ink). */
  cycle: readonly string[] | null = null;
  private cycleAt = 0;
  /** Mirror mode: every stroke, stamp and fill is repeated on the other half of the page. */
  mirror = false;
  stamp: StampId = 'star';
  /** Brush look for the pencil (and stamps); 'pixel' snaps everything to size×size squares. */
  brush: BrushKind = 'pencil';
  /** Pixel squares already in the stroke being drawn. */
  private cells = new Set<number>();
  dirty = false;

  constructor() {
    super();
    this.res = 512;
    this.el = document.createElement('canvas');
    this.el.width = this.res;
    this.el.height = this.res;
    this.el.className = 'drawing-canvas';
    this.el.setAttribute('role', 'img');
    this.el.setAttribute('aria-label', t('Drawing area. Draw with mouse, finger or stylus.'));
    this.el.style.touchAction = 'none';
    const ctx = this.el.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable');
    this.ctx = ctx;
    this.el.addEventListener('pointerdown', this.onDown);
    this.el.addEventListener('pointermove', this.onMove);
    this.el.addEventListener('pointerup', this.onUp);
    this.el.addEventListener('pointercancel', this.onUp);
    this.el.addEventListener('lostpointercapture', this.onUp);
  }

  get tool(): DrawTool {
    return this.toolValue;
  }
  set tool(v: DrawTool) {
    this.toolValue = v;
    this.el.classList.toggle('tool-move', v === 'move');
    if (v !== 'move') this.select([]);
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }
  get selectedCount(): number {
    return this.selection.length;
  }

  load(d: DrawingDTO | null): void {
    this.strokes = d ? d.strokes.map((s) => ({ ...s, p: s.p.slice() })) : [];
    this.groups = this.strokes.map(() => this.nextGroup++);
    this.undoStack = [];
    this.redoStack = [];
    this.dirty = false;
    this.select([]);
  }

  getDrawing(): DrawingDTO {
    return { ...emptyDrawing(), strokes: this.strokes.map((s) => ({ ...s, p: s.p.slice() })) };
  }

  undo(): void {
    const e = this.undoStack.pop();
    if (!e) return;
    this.apply(e, true);
    this.redoStack.push(e);
    this.changed();
  }

  redo(): void {
    const e = this.redoStack.pop();
    if (!e) return;
    this.apply(e, false);
    this.undoStack.push(e);
    this.changed();
  }

  /** Plays a history entry forwards, or backwards when undoing. */
  private apply(e: HistoryEntry, back: boolean): void {
    if (e.kind === 'add') {
      if (back) {
        this.strokes.splice(-e.strokes.length);
        this.groups.splice(-e.strokes.length);
      } else {
        this.strokes.push(...e.strokes);
        this.groups.push(...e.strokes.map(() => e.group));
      }
      this.selection = [];
    } else if (e.kind === 'clear') {
      this.strokes = back ? e.prev : [];
      this.groups = back ? e.prevGroups : [];
      this.selection = [];
    } else if (e.kind === 'move') {
      this.strokes = moved(this.strokes, e.idx, back ? -e.dx : e.dx, back ? -e.dy : e.dy);
      this.selection = e.idx.slice();
    } else if (e.kind === 'order') {
      // order[k] = old index of the stroke now at k; undo applies the inverse.
      const picked = new Set(this.selection.map((i) => (back ? e.order[i]! : e.order.indexOf(i))));
      if (back) {
        const strokes: StrokeDTO[] = [];
        const groups: number[] = [];
        e.order.forEach((old, k) => {
          strokes[old] = this.strokes[k]!;
          groups[old] = this.groups[k]!;
        });
        this.strokes = strokes;
        this.groups = groups;
      } else {
        this.strokes = e.order.map((old) => this.strokes[old]!);
        this.groups = e.order.map((old) => this.groups[old]!);
      }
      this.selection = [...picked].filter((i) => i >= 0).sort((a, b) => a - b);
    } else {
      if (back) {
        for (const it of e.items) {
          this.strokes.splice(it.at, 0, it.stroke);
          this.groups.splice(it.at, 0, it.group);
        }
        this.selection = e.items.map((it) => it.at);
      } else {
        for (const it of [...e.items].reverse()) {
          this.strokes.splice(it.at, 1);
          this.groups.splice(it.at, 1);
        }
        this.selection = [];
      }
    }
    this.emit('selection', this.selection.length);
  }

  clearAll(): void {
    if (this.strokes.length === 0) return;
    this.undoStack.push({ kind: 'clear', prev: this.strokes, prevGroups: this.groups });
    this.redoStack = [];
    this.strokes = [];
    this.groups = [];
    this.selection = [];
    this.emit('selection', 0);
    this.changed();
  }

  /** Validate the complete group before committing; one stamp or fill is one undo step. */
  private commitGroup(strokes: StrokeDTO[]): boolean {
    // Stamps and fills are ink: they leave the eraser's reserve free.
    if (!fitsLimits([...this.strokes, ...strokes], false)) {
      this.emit('limit', t('The page is full! The eraser still works: rub out a bit to make room.'));
      return false;
    }
    const err = validateDrawing({ w: GRID, h: GRID, strokes: [...this.strokes, ...strokes] });
    if (err) {
      this.emit('limit', err);
      return false;
    }
    const entry: HistoryEntry = { kind: 'add', strokes, group: this.nextGroup++ };
    this.apply(entry, false);
    this.undoStack.push(entry);
    this.redoStack = [];
    this.changed();
    return true;
  }

  /** The ink for the next stroke: the pen colour, or the next colour of an RGB pen. */
  private nextInk(): string {
    if (!this.cycle?.length) return this.color;
    this.color = this.cycle[this.cycleAt++ % this.cycle.length]!;
    return this.color;
  }

  /** In mirror mode a stroke also gets its reflection (unless it lies on the centre line). */
  private withMirror(strokes: StrokeDTO[]): StrokeDTO[] {
    if (!this.mirror) return strokes;
    const mirrored = strokes.map(mirrorStroke).filter((m, i) => m.p.some((v, k) => v !== strokes[i]!.p[k]));
    return [...strokes, ...mirrored];
  }

  /** The `b` field for a new stroke of the current brush (the pencil leaves it out, keeping old drawings byte-identical). */
  private get look(): BrushKind | undefined {
    return this.brush === 'pencil' ? undefined : this.brush;
  }

  placeStamp(x: number, y: number): boolean {
    // Stamps are outlines: they take the brush look, but stay outlines in pixel mode.
    const b = this.brush === 'pixel' ? undefined : this.look;
    const size = this.brush === 'pixel' ? Math.max(3, this.size >> 1) : this.size;
    return this.commitGroup(this.withMirror(stampStrokes(this.stamp, x, y, this.nextInk(), size).map((st) => (b ? { ...st, b } : st))));
  }

  /** The paint bucket at grid point (x, y); in mirror mode the reflected point is filled too. */
  placeFill(x: number, y: number): boolean {
    const pixels = this.rasterize();
    const ink = this.nextInk();
    if (this.brush === 'pixel') return this.placePixelFill(pixels, x, y, ink);
    const first = fillAt(pixels, x, y, ink);
    if ('error' in first) {
      if (first.error === 'leak') this.emit('limit', t('The paint would spill out of the picture! Close the outline first, then fill inside it.'));
      return false;
    }
    const strokes = [...first.strokes];
    const mx = GRID - x;
    const seed = Math.min(GRID - 1, Math.max(0, Math.floor(y))) * GRID + Math.min(GRID - 1, Math.max(0, Math.floor(mx)));
    if (this.mirror && !first.region[seed]) {
      const second = fillAt(pixels, mx, y, ink);
      if (!('error' in second)) strokes.push(...second.strokes);
    }
    return this.commitGroup(strokes);
  }

  private placePixelFill(pixels: Uint8ClampedArray, x: number, y: number, ink: string): boolean {
    const cell = this.size;
    const first = pixelFillAt(pixels, x, y, cell, ink);
    if ('error' in first) {
      if (first.error === 'leak') this.emit('limit', t('The paint would spill out of the picture! Close the outline first, then fill inside it.'));
      return false;
    }
    const strokes = [...first.strokes];
    const C = Math.floor(GRID / cell);
    const mx = GRID - x;
    const seed = Math.min(C - 1, Math.max(0, Math.floor(y / cell))) * C + Math.min(C - 1, Math.max(0, Math.floor(mx / cell)));
    if (this.mirror && !first.region[seed]) {
      const second = pixelFillAt(pixels, mx, y, cell, ink);
      if (!('error' in second)) strokes.push(...second.strokes);
    }
    return this.commitGroup(strokes);
  }

  /** Removes the selected strokes (one undo step). */
  deleteSelection(): void {
    if (this.selection.length === 0) return;
    const items = [...this.selection].sort((a, b) => a - b).map((at) => ({ at, stroke: this.strokes[at]!, group: this.groups[at]! }));
    const entry: HistoryEntry = { kind: 'remove', items };
    this.apply(entry, false);
    this.undoStack.push(entry);
    this.redoStack = [];
    this.changed();
  }

  /** Layer order for the selection: to the front/back, or one piece up/down (one undo step). */
  arrange(how: Arrange): void {
    if (this.selection.length === 0) return;
    const order = arrangeOrder(this.groups, this.selection, how);
    if (order.every((old, k) => old === k)) return;
    const entry: HistoryEntry = { kind: 'order', order };
    this.apply(entry, false);
    this.undoStack.push(entry);
    this.redoStack = [];
    this.changed();
  }

  /** Redraws the page after the brush or size changed (the pixel grid follows them). */
  repaint(): void {
    this.redraw();
  }

  /** Selects the whole drawing (move tool). */
  selectAll(): void {
    this.select(this.strokes.map((_, i) => i));
  }

  private select(idx: number[]): void {
    this.selection = idx;
    this.emit('selection', idx.length);
    this.redraw();
  }

  /** The drawing at grid resolution without line boil, for the paint bucket. */
  private rasterize(): Uint8ClampedArray {
    const c = document.createElement('canvas');
    c.width = GRID;
    c.height = GRID;
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    renderDrawing(ctx, { w: GRID, h: GRID, strokes: this.strokes }, GRID);
    return ctx.getImageData(0, 0, GRID, GRID).data;
  }

  private changed(): void {
    this.dirty = true;
    this.redraw();
    this.emit('change', this.getDrawing());
  }

  private redraw(): void {
    this.ctx.clearRect(0, 0, this.res, this.res);
    const strokes = this.drag && (this.drag.dx || this.drag.dy) ? moved(this.strokes, this.selection, this.drag.dx, this.drag.dy) : this.strokes;
    renderDrawing(this.ctx, { w: GRID, h: GRID, strokes }, this.res);
    if (this.brush === 'pixel' && this.tool !== 'move') this.drawPixelGrid();
    // Selection frame and the box being dragged out: on screen only, never saved.
    const k = this.res / GRID;
    const frame = (b: Box, colour: string): void => {
      this.ctx.save();
      this.ctx.setLineDash([8, 6]);
      this.ctx.lineWidth = 3;
      this.ctx.strokeStyle = colour;
      this.ctx.strokeRect(Math.min(b.x0, b.x1) * k, Math.min(b.y0, b.y1) * k, Math.abs(b.x1 - b.x0) * k, Math.abs(b.y1 - b.y0) * k);
      this.ctx.restore();
    };
    const sel = this.selection.length ? boundsOf(strokes, this.selection) : null;
    if (sel) frame({ x0: sel.x0 - 3, y0: sel.y0 - 3, x1: sel.x1 + 3, y1: sel.y1 + 3 }, '#2e86de');
    if (this.box) frame(this.box, '#8e5bd6');
  }

  /** Faint squares to paint in (on screen only, never saved). */
  private drawPixelGrid(): void {
    const k = this.res / GRID;
    const step = this.size * k;
    this.ctx.save();
    this.ctx.strokeStyle = 'rgba(46, 134, 222, 0.16)';
    this.ctx.lineWidth = 1;
    this.ctx.beginPath();
    for (let v = step; v < this.res - 0.5; v += step) {
      const a = Math.round(v) + 0.5;
      this.ctx.moveTo(a, 0);
      this.ctx.lineTo(a, this.res);
      this.ctx.moveTo(0, a);
      this.ctx.lineTo(this.res, a);
    }
    this.ctx.stroke();
    this.ctx.restore();
  }

  /** Grid point to the top-left corner of its pixel square. */
  private cellOf(x: number, y: number): [number, number] {
    const c = this.size;
    const max = GRID - c;
    return [Math.min(max, Math.floor(x / c) * c), Math.min(max, Math.floor(y / c) * c)];
  }

  /** Adds the squares on the line from the last square to (x, y), so fast swipes leave no gaps. */
  private addCells(x: number, y: number): number[] {
    const cur = this.current!;
    const c = this.size;
    const [tx, ty] = this.cellOf(x, y);
    const lx = cur.p[cur.p.length - 2]!;
    const ly = cur.p[cur.p.length - 1]!;
    const steps = Math.max(Math.abs(tx - lx), Math.abs(ty - ly)) / c;
    const added: number[] = [];
    for (let i = 1; i <= steps; i++) {
      const [cx, cy] = this.cellOf(lx + ((tx - lx) * i) / steps + c / 2, ly + ((ty - ly) * i) / steps + c / 2);
      const key = cy * (GRID + 1) + cx;
      if (this.cells.has(key)) continue;
      this.cells.add(key);
      cur.p.push(cx, cy);
      added.push(cx, cy);
    }
    return added;
  }

  private toGrid(ev: PointerEvent): [number, number] {
    const r = this.el.getBoundingClientRect();
    const x = Math.round(((ev.clientX - r.left) / r.width) * GRID);
    const y = Math.round(((ev.clientY - r.top) / r.height) * GRID);
    return [Math.min(GRID, Math.max(0, x)), Math.min(GRID, Math.max(0, y))];
  }

  private onDown = (ev: PointerEvent): void => {
    if (this.pointerId !== null || (ev.pointerType === 'mouse' && ev.button !== 0)) return;
    if (this.tool === 'stamp' || this.tool === 'fill') {
      ev.preventDefault();
      const [x, y] = this.toGrid(ev);
      if (this.tool === 'stamp') this.placeStamp(x, y);
      else this.placeFill(x, y);
      return;
    }
    if (this.tool === 'move') {
      ev.preventDefault();
      this.pointerId = ev.pointerId;
      this.el.setPointerCapture(ev.pointerId);
      const [x, y] = this.toGrid(ev);
      const sel = this.selection.length ? boundsOf(this.strokes, this.selection) : null;
      const inSel = sel !== null && x >= sel.x0 - 6 && x <= sel.x1 + 6 && y >= sel.y0 - 6 && y <= sel.y1 + 6;
      if (!inSel) {
        // Pressing on a piece picks it up at once; pressing on paper starts a selection box.
        const hit = hitStroke(this.strokes, x, y);
        if (hit < 0) {
          this.box = { x0: x, y0: y, x1: x, y1: y };
          this.select([]);
          return;
        }
        this.select(withGroups([hit], this.groups));
      }
      this.drag = { sx: x, sy: y, dx: 0, dy: 0 };
      return;
    }
    const erasing = this.tool === 'eraser';
    if (this.strokes.length >= DRAWING_LIMITS.maxStrokes - (erasing ? 0 : ERASER_RESERVE.strokes)) {
      this.emit('limit', erasing ? t('The page is completely full. Undo or clear a bit first.') : t('The page is full! The eraser still works: rub out a bit to make room.'));
      return;
    }
    ev.preventDefault();
    this.pointerId = ev.pointerId;
    this.el.setPointerCapture(ev.pointerId);
    const [x, y] = this.toGrid(ev);
    if (this.brush === 'pixel') {
      const [cx, cy] = this.cellOf(x, y);
      this.cells = new Set([cy * (GRID + 1) + cx]);
      this.current = { c: this.tool === 'eraser' ? '#000000' : this.nextInk(), s: this.size, e: this.tool === 'eraser', p: [cx, cy], b: 'pixel' };
      this.preview([cx, cy]);
      return;
    }
    const pressure = ev.pointerType === 'pen' && ev.pressure > 0 ? 0.6 + ev.pressure * 0.8 : 1;
    const size = Math.max(DRAWING_LIMITS.minBrush, Math.min(DRAWING_LIMITS.maxBrush, Math.round(this.size * (this.tool === 'eraser' ? 1.8 : 1) * pressure)));
    this.current = { c: this.tool === 'eraser' ? '#000000' : this.nextInk(), s: size, e: this.tool === 'eraser', p: [x, y] };
    if (this.look && this.tool !== 'eraser') this.current.b = this.look;
    this.preview([x, y, x, y]);
  };

  /** Draws a live segment (and its reflection in mirror mode) before the stroke is finished. */
  private preview(p: number[]): void {
    if (!this.current) return;
    const seg = { ...this.current, p };
    drawStroke(this.ctx, seg, this.res / GRID, null, 0);
    if (this.mirror) drawStroke(this.ctx, mirrorStroke(seg), this.res / GRID, null, 0);
  }

  private onMove = (ev: PointerEvent): void => {
    if (ev.pointerId !== this.pointerId) return;
    if (this.drag) {
      const [x, y] = this.toGrid(ev);
      const b = pointBounds(this.strokes, this.selection);
      if (!b) return;
      [this.drag.dx, this.drag.dy] = clampMove(b, x - this.drag.sx, y - this.drag.sy);
      this.redraw();
      return;
    }
    if (this.box) {
      const [x, y] = this.toGrid(ev);
      this.box = { ...this.box, x1: x, y1: y };
      this.redraw();
      return;
    }
    if (!this.current) return;
    const events = typeof ev.getCoalescedEvents === 'function' ? ev.getCoalescedEvents() : [ev];
    const p = this.current.p;
    for (const e of events.length ? events : [ev]) {
      const [x, y] = this.toGrid(e);
      if (this.current.b === 'pixel') {
        const added = this.addCells(x, y);
        if (added.length) this.preview(added);
        continue;
      }
      const lx = p[p.length - 2]!;
      const ly = p[p.length - 1]!;
      if (x === lx && y === ly) continue;
      p.push(x, y);
      // Incremental segment so drawing feels instant; the full redraw on
      // pointerup applies the final smoothed pencil look.
      this.preview([lx, ly, x, y]);
    }
  };

  private onUp = (ev: PointerEvent): void => {
    if (ev.pointerId !== this.pointerId) return;
    if (this.drag) {
      this.pointerId = null;
      const { dx, dy } = this.drag;
      this.drag = null;
      if (dx === 0 && dy === 0) {
        this.redraw();
        return;
      }
      const entry: HistoryEntry = { kind: 'move', idx: this.selection.slice(), dx, dy };
      this.apply(entry, false);
      this.undoStack.push(entry);
      this.redoStack = [];
      this.changed();
      return;
    }
    if (this.box) {
      this.pointerId = null;
      const box = this.box;
      this.box = null;
      this.select(withGroups(strokesInBox(this.strokes, box), this.groups));
      return;
    }
    if (!this.current) return;
    this.pointerId = null;
    const stroke = this.current;
    this.current = null;
    if (stroke.b !== 'pixel') {
      stroke.p = simplify(stroke.p, 0.6);
      if (stroke.p.length === 2) stroke.p.push(stroke.p[0]!, stroke.p[1]!);
    }
    const group = this.withMirror([stroke]);
    if (!fitsLimits([...this.strokes, ...group], stroke.e)) {
      this.emit('limit', stroke.e ? t('The page is completely full. Undo or clear a bit first.') : t('The page is full! The eraser still works: rub out a bit to make room.'));
      this.redraw();
      return;
    }
    const entry: HistoryEntry = { kind: 'add', strokes: group, group: this.nextGroup++ };
    this.apply(entry, false);
    this.undoStack.push(entry);
    this.redoStack = [];
    this.changed();
  };

  dispose(): void {
    this.el.removeEventListener('pointerdown', this.onDown);
    this.el.removeEventListener('pointermove', this.onMove);
    this.el.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('pointercancel', this.onUp);
    this.el.removeEventListener('lostpointercapture', this.onUp);
    this.clear();
  }
}
